import { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { themeable } from './utils/colors';

export type ButtonProps = PropsWithChildren<{
  size?: 'small' | 'medium' | 'large';
  $color?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger';
  outline?: boolean;
}> &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

const StyledButton = styled.button<ButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  font-family: 'Roboto';
  font-weight: normal;
  line-height: normal;

  border: 2px solid transparent;
  border-radius: 2px;
  position: relative;

  transition: background-color 200ms, color 200ms, box-shadow 200ms;

  // Size
  ${({ size }) => {
    switch (size) {
      case 'small':
        return css`
          padding: 6px 10px;
          font-size: 14px;
        `;
      case 'large':
        return css`
          padding: 14px 28px;
          font-size: 20px;
        `;
      default:
        return css`
          padding: 10px 20px;
          font-size: 16px;
        `;
    }
  }}

  // Color
  ${({ theme, $color, outline }) => {
    const themedColor = themeable(theme, $color);
    if (outline) {
      let bgColor = 'transparent';
      let borderColor = themedColor;
      if ($color === 'primary') {
        bgColor = theme.colors.N50;
      } else if (!$color) {
        borderColor = theme.colors.N100;
      }
      return css`
        background-color: ${bgColor};
        border-color: ${borderColor};
        color: ${themedColor};

        &:hover,
        &:focus {
          background-color: ${themedColor};
          color: #fff;
        }

        &:active {
          background: black;
        }
      `;
    } else {
      return css`
        background-color: ${themedColor || theme.colors.N80};
        color: ${themedColor ? '#fff' : theme.colors.N30};

        &:hover {
          box-shadow: inset 0 0 100px rgba(0, 0, 0, 0.2);
        }

        ${$color === 'primary' &&
        css`
          border-color: white;
        `};
      `;
    }
  }}
`;

export function Button({
  size,
  $color,
  outline,
  children,
  ...rest
}: ButtonProps) {
  return (
    <StyledButton size={size} $color={$color} outline={outline} {...rest}>
      {children}
    </StyledButton>
  );
}

export default Button;
